import React, { Suspense, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { Upload, X } from 'lucide-react';
import { uploadQueue } from '../services/uploadQueue';
import { getApiErrorMessage } from '../services/api';
import { googleDriveUploadFeatureConfig } from '../config/googleDriveUpload';
import { loadGoogleIdentityServices, requestGoogleDriveAccessToken } from '../services/googleIdentityServices';
import { LOCAL_MEDIA_FILE_ACCEPT, MEDIA_UPLOAD_QUEUE_MAX_ITEMS } from '../utils/mediaUploadPolicy';
import ProcessingProgressBar from './ProcessingProgressBar';
import './UploadQueueModal.css';

const GoogleDrivePickerDialog = React.lazy(() => import('./GoogleDrivePickerDialog'));

const formatTransferBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  const unit = Math.min(3, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${Number((bytes / 1024 ** unit).toFixed(1))} ${['B', 'KiB', 'MiB', 'GiB'][unit]}`;
};

export function UploadQueueLauncher() {
  // Byte callbacks must not rerender the entire workspace/header tree.
  useSyncExternalStore(uploadQueue.subscribe, () => {
    const state = uploadQueue.getSnapshot();
    return `${state.items.length}:${state.items.filter((item) => !['completed', 'failed'].includes(item.state)).length}:${state.indexId}`;
  });
  const state = uploadQueue.getSnapshot();
  if (!state.items.length) return null;
  const active = state.items.some((item) => !['completed', 'failed'].includes(item.state));
  return <button type="button" className="app-header-mcp-button" onClick={() => uploadQueue.open(state.indexId, state.indexName)}>Uploads{active ? ' •' : ''}</button>;
}

export default function UploadQueueModal() {
  const queue = useSyncExternalStore(uploadQueue.subscribe, uploadQueue.getSnapshot);
  const [urls, setUrls] = useState('');
  const [importingUrls, setImportingUrls] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [driveToken, setDriveToken] = useState<string | null>(null);
  const [loadingDrive, setLoadingDrive] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const dialog = useRef<HTMLDivElement>(null);
  const contextKey = uploadQueue.getContextKey();
  const driveContext = useRef<string | null>(null);
  useEffect(() => { setDriveToken(null); setError(null); setLoadingDrive(false); setUrls(''); setImportingUrls(false); }, [contextKey]);
  const activeCount = queue.items.filter((item) => !['completed', 'failed'].includes(item.state)).length;
  useEffect(() => {
    if (!queue.open) { setDriveToken(null); return; }
    const previous = document.activeElement as HTMLElement | null;
    dialog.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !driveToken) uploadQueue.close();
      if (event.key !== 'Tab' || driveToken) return;
      const focusable = dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled),input:not(:disabled),textarea:not(:disabled),[tabindex="0"]');
      if (!focusable?.length) return;
      const first = focusable[0]; const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); previous?.focus(); };
  }, [queue.open, driveToken]);

  const selectFiles = (files: File[]) => {
    try { uploadQueue.addFiles(files); setError(null); } catch (failure) { setError(getApiErrorMessage(failure, 'Could not add these files.')); }
  };
  const importUrls = async (event: React.FormEvent) => {
    event.preventDefault();
    const requestContext = uploadQueue.getContextKey();
    setImportingUrls(true); setError(null);
    try {
      await uploadQueue.importUrls(urls.split(/\r?\n/));
      if (requestContext === uploadQueue.getContextKey()) setUrls('');
    } catch (failure) {
      if (requestContext === uploadQueue.getContextKey()) setError(getApiErrorMessage(failure, 'Could not import these URLs.'));
    } finally {
      if (requestContext === uploadQueue.getContextKey()) setImportingUrls(false);
    }
  };
  const connectDrive = async (resumeId?: string) => {
    if (googleDriveUploadFeatureConfig.status !== 'enabled') return;
    const requestContext = uploadQueue.getContextKey();
    const current = () => requestContext === uploadQueue.getContextKey() && uploadQueue.getSnapshot().open;
    setLoadingDrive(true); setError(null);
    try {
      await loadGoogleIdentityServices();
      const grant = await requestGoogleDriveAccessToken(googleDriveUploadFeatureConfig.config.clientId, 'select_account');
      if (!current()) return;
      if (resumeId) uploadQueue.reconnectDrive(resumeId, grant.accessToken);
      else { driveContext.current = requestContext; setDriveToken(grant.accessToken); }
    } catch (failure) { if (current()) setError(getApiErrorMessage(failure, 'Could not connect Google Drive.')); }
    finally { if (current()) setLoadingDrive(false); }
  };

  if (!queue.open) return null;
  return createPortal(<div className="upload-queue-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) uploadQueue.close(); }}>
    <div className="upload-queue-dialog" role="dialog" aria-modal="true" aria-labelledby="upload-queue-title" tabIndex={-1} ref={dialog}>
      <header><div><h2 id="upload-queue-title">Add media</h2><p>Add media to {queue.indexName || 'your index'}</p></div>
        <button type="button" aria-label="Close uploads" onClick={() => uploadQueue.close()}><X size={20} /></button></header>
      {!queue.storageAvailable && <p role="alert">Browser storage is unavailable. Keep this page open until transfers finish.</p>}
      {error && <p className="upload-queue-error" role="alert">{error}</p>}
      <div className="upload-queue-drop" onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => { event.preventDefault(); selectFiles(Array.from(event.dataTransfer.files)); }}>
        <Upload size={24} aria-hidden="true" /><span>Drop media here or choose files</span>
        <input ref={input} type="file" multiple accept={LOCAL_MEDIA_FILE_ACCEPT} hidden aria-label="Choose media files"
          onChange={(event) => { selectFiles(Array.from(event.target.files || [])); event.target.value = ''; }} />
        <button type="button" disabled={activeCount >= MEDIA_UPLOAD_QUEUE_MAX_ITEMS} onClick={() => input.current?.click()}>Choose files</button>
        {googleDriveUploadFeatureConfig.status === 'enabled' && <button type="button" disabled={loadingDrive || activeCount >= MEDIA_UPLOAD_QUEUE_MAX_ITEMS}
          onClick={() => void connectDrive()}>{loadingDrive ? 'Connecting…' : 'Google Drive'}</button>}
      </div>
      <form className="upload-queue-urls" onSubmit={importUrls}>
        <label htmlFor="upload-media-urls">Or import from URLs</label>
        <textarea id="upload-media-urls" value={urls} onChange={(event) => setUrls(event.target.value)} rows={2}
          placeholder="Paste media or YouTube URLs, one per line" disabled={importingUrls} />
        <button type="submit" disabled={importingUrls || !urls.trim()}>{importingUrls ? 'Starting import…' : 'Import URLs'}</button>
      </form>
      <div className="upload-queue-items">
        {queue.items.length === 0 && <p>Your files, URLs, and their progress will appear here.</p>}
        {queue.items.map((item) => {
          const saving = item.state === 'saving' || (item.source === 'drive' && item.state === 'uploading' && item.phase === 'saving');
          const preparing = item.source === 'local' && item.state === 'uploading' && item.phase === 'preparing_upload';
          const transferring = item.state === 'uploading' && !saving && !preparing;
          const percent = item.size > 0 ? Math.min(100, Math.floor(item.bytes / item.size * 1000) / 10) : null;
          const label = transferring ? item.source === 'drive' ? 'Copying from Drive' : 'Uploading'
            : preparing ? 'Preparing upload'
            : item.state === 'needs_file' ? 'Reselect file to resume' : item.state === 'needs_drive' ? 'Reconnect Drive to retry'
            : item.state === 'selected' ? 'Ready to upload' : item.state === 'completed' ? item.source === 'url' ? 'Imported' : 'Uploaded' : item.state === 'failed' ? 'Failed'
            : saving ? 'Saving' : 'Preparing media';
          return <article className="upload-queue-item" key={item.id} data-state={item.state}>
            <div className="upload-queue-item-heading">{item.thumbnailData && <img className="upload-queue-poster" src={item.thumbnailData} alt="" />}<strong title={item.name}>{item.name}</strong><span>{item.indexName}</span>
              {!['uploading', 'saving', 'processing'].includes(item.state) && <button type="button" aria-label={`Remove ${item.name}`} onClick={() => uploadQueue.remove(item.id)}><X size={15} /></button>}</div>
            {item.state === 'selected' && <label className="upload-queue-title-label">Title<input value={item.title} onChange={(event) => uploadQueue.setTitle(item.id, event.target.value)} /></label>}
            {item.job?.file_previews?.map((file) => <div key={file.file_id} className="upload-queue-file-preview">{file.thumbnail_uri && <img className="upload-queue-poster" src={file.thumbnail_uri} alt="" />}<span>{file.file_name}</span>{file.status && <small>{file.status}</small>}</div>)}
            {item.state === 'processing' ? <ProcessingProgressBar status={item.job?.status || 'importing'} phase={item.job?.phase} lastWorkerActivityAt={item.job?.liveness?.last_heartbeat_at}
              completed={item.job?.progress.parts_completed} total={item.job?.progress.parts_total} unit="parts" />
              : <div className="upload-queue-status"><span>{label}</span>{transferring && percent !== null && <strong>{percent === 0 && item.bytes > 0 ? '<0.1' : percent}%</strong>}</div>}
            {transferring && <progress aria-label={`${item.name} upload`} value={item.size ? item.bytes : undefined} max={item.size || undefined} />}
            {transferring && <div className="upload-queue-bytes">{formatTransferBytes(item.bytes)} of {formatTransferBytes(item.size)} {item.source === 'drive' ? 'copied' : 'sent'}
              {item.source === 'local' && <> · {formatTransferBytes(item.confirmed)} confirmed</>}</div>}
            {item.state === 'needs_file' && <div className="upload-queue-bytes">{formatTransferBytes(item.confirmed)} of {formatTransferBytes(item.size)} confirmed. Reselect the same file to check storage and continue.</div>}
            {item.error && <p className="upload-queue-error" role="alert">{item.error}</p>}
            {item.restartRequired && uploadQueue.canRestart(item.id) && <button type="button" onClick={() => uploadQueue.restart(item.id)}>Restart upload from 0%</button>}
            {item.state === 'needs_file' && <label className="upload-queue-reselect">Reselect file<input type="file" accept={LOCAL_MEDIA_FILE_ACCEPT}
              aria-label={`Reselect ${item.name}`} onChange={(event) => {
                const file = event.target.files?.[0]; if (file) void uploadQueue.reselect(item.id, file).catch((failure) => setError(getApiErrorMessage(failure, 'The file could not be resumed.')));
                event.target.value = '';
              }} /></label>}
            {item.state === 'needs_drive' && <button type="button" disabled={loadingDrive} onClick={() => void connectDrive(item.id)}>Reconnect Drive</button>}
          </article>;
        })}
      </div>
      <footer><span>Uploads and imports continue when you switch views. After refresh, reselect unfinished local files.</span>
        <button type="button" className="upload-queue-start" disabled={!queue.items.some((item) => item.state === 'selected')} onClick={() => uploadQueue.start()}>Upload selected</button></footer>
    </div>
    {driveToken && googleDriveUploadFeatureConfig.status === 'enabled' && <Suspense fallback={null}><GoogleDrivePickerDialog
      config={googleDriveUploadFeatureConfig.config} accessToken={driveToken} maxItems={Math.max(0, MEDIA_UPLOAD_QUEUE_MAX_ITEMS - activeCount)}
      onCanceled={() => setDriveToken(null)} onError={() => { setDriveToken(null); setError('Google Drive could not be opened. Try again.'); }}
      onPicked={(selection) => {
        if (driveContext.current !== uploadQueue.getContextKey()) return;
        try {
          if (selection.exceedsMaximum) throw new Error(`Select at most ${MEDIA_UPLOAD_QUEUE_MAX_ITEMS - activeCount} files.`);
          uploadQueue.addDriveFiles(selection.files, driveToken);
          setError(selection.rejectedCount ? `${selection.rejectedCount} unsupported files were not added.` : null);
        } catch (failure) { setError(getApiErrorMessage(failure, 'Could not add Drive files.')); }
        setDriveToken(null);
      }} /></Suspense>}
  </div>, document.body);
}
